import { supabase } from "../../../supabaseClient";

const ACTIVE_CHATBOT_STORAGE_KEY = "active_chatbot_id";
const ACTIVE_CHATBOT_SCOPED_STORAGE_KEY = "active_chatbot_ids";
const ACTIVE_TENANT_STORAGE_KEY = "active_tenant_id";
const ACTIVE_TENANT_SCOPED_STORAGE_KEY = "active_tenant_ids";
const ACTIVE_CHATBOT_LEGACY_OWNER_KEY = "active_chatbot_owner";
const ACTIVE_TENANT_LEGACY_OWNER_KEY = "active_tenant_owner";
const PUBLIC_WIDGET_SESSION_STORAGE_KEY = "public_widget_session";
const PUBLIC_WIDGET_SESSIONS_STORAGE_KEY = "public_widget_sessions";

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeScope(scope = null) {
  if (!scope) return {};
  if (typeof scope === "string") {
    return {
      publicBotId: scope,
      tenantId: scope,
    };
  }
  return scope;
}

function decodeBase64Url(value) {
  const normalized = String(value || "")
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(String(value || "").length / 4) * 4, "=");

  if (typeof window !== "undefined" && typeof window.atob === "function") {
    try {
      return window.atob(normalized);
    } catch {
      return "";
    }
  }

  try {
    return Buffer.from(normalized, "base64").toString("utf8");
  } catch {
    return "";
  }
}

function getUserIdFromJwt(token) {
  const parts = String(token || "").split(".");
  if (parts.length < 2) return null;

  const payload = safeJsonParse(decodeBase64Url(parts[1]));
  return payload?.sub || payload?.user_id || payload?.userId || null;
}

function getCurrentSupabaseUserIdFromStorage() {
  if (!canUseStorage()) return null;

  const keys = Object.keys(window.localStorage);
  for (const key of keys) {
    if (!/auth-token/i.test(key)) continue;

    const parsed = safeJsonParse(window.localStorage.getItem(key));
    if (!parsed) continue;

    const candidates = [
      parsed?.currentSession?.user?.id,
      parsed?.session?.user?.id,
      parsed?.user?.id,
      Array.isArray(parsed) ? parsed[0]?.user?.id : null,
    ].filter(Boolean);

    if (candidates.length > 0) {
      return candidates[0];
    }
  }

  return null;
}

function resolveUserScope(scope = null) {
  const normalizedScope = normalizeScope(scope);
  return (
    normalizedScope.userId ||
    getUserIdFromJwt(normalizedScope.token) ||
    getCurrentSupabaseUserIdFromStorage() ||
    "anonymous"
  );
}

function getActiveTenantScopedKey(scope = null) {
  const userScope = resolveUserScope(scope);
  return `${ACTIVE_TENANT_SCOPED_STORAGE_KEY}:${userScope}`;
}

export function setActiveTenantId(tenantId, scope = null) {
  if (!canUseStorage()) return;

  const normalizedScope = normalizeScope(scope);
  const userScope = resolveUserScope(normalizedScope);
  const scopedKey = getActiveTenantScopedKey(normalizedScope);

  if (!tenantId) {
    window.localStorage.removeItem(scopedKey);
    window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    window.localStorage.removeItem(ACTIVE_TENANT_LEGACY_OWNER_KEY);
    return;
  }

  const normalizedValue = String(tenantId);
  window.localStorage.setItem(scopedKey, normalizedValue);

  // Legacy fallback keys (guarded by owner)
  window.localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, normalizedValue);
  window.localStorage.setItem(ACTIVE_TENANT_LEGACY_OWNER_KEY, String(userScope || 'anonymous'));
}

export function getActiveTenantId(scope = null) {
  if (!canUseStorage()) return null;

  const scopedKey = getActiveTenantScopedKey(scope);
  const scopedValue = window.localStorage.getItem(scopedKey);
  if (scopedValue) {
    return scopedValue;
  }

  const legacyValue = window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  const userScope = resolveUserScope(scope);
  const legacyOwner = window.localStorage.getItem(ACTIVE_TENANT_LEGACY_OWNER_KEY);

  if (!legacyValue) {
    return null;
  }

  // Never expose or migrate legacy values across users (multi-tenant safety).
  // If the current user cannot be resolved yet, fail closed instead of leaking a stale tenant.
  if (userScope === "anonymous") {
    return legacyOwner === "anonymous" ? legacyValue : null;
  }

  if (legacyOwner && legacyOwner !== userScope) {
    return null;
  }

  if (!legacyOwner) {
    window.localStorage.setItem(ACTIVE_TENANT_LEGACY_OWNER_KEY, String(userScope));
  }

  window.localStorage.setItem(scopedKey, legacyValue);
  return legacyValue;
}

export function clearActiveTenantId(scope = null) {
  if (!canUseStorage()) return;

  window.localStorage.removeItem(getActiveTenantScopedKey(scope));
  window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  window.localStorage.removeItem(ACTIVE_TENANT_LEGACY_OWNER_KEY);
}

function resolveTenantScope(scope = null) {
  const normalizedScope = normalizeScope(scope);
  return normalizedScope.tenantId || getActiveTenantId(normalizedScope) || "default";
}

function getActiveChatbotScopedKey(scope = null) {
  const userScope = resolveUserScope(scope);
  const tenantScope = resolveTenantScope(scope);
  return `${ACTIVE_CHATBOT_SCOPED_STORAGE_KEY}:${userScope}:${tenantScope}`;
}

function getPublicWidgetSessionScopedKey(scope = null) {
  const normalizedScope = normalizeScope(scope);
  const publicBotId =
    normalizedScope.publicBotId ||
    (typeof window !== "undefined" ? window.__CHATBOT_PUBLIC_CONTEXT__?.publicBotId : null) ||
    "default";

  return `${PUBLIC_WIDGET_SESSIONS_STORAGE_KEY}:${String(publicBotId)}`;
}

export async function getAuthToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

export async function getCurrentUserId() {
  const { data } = await supabase.auth.getUser();
  return data.user?.id || "default";
}

export async function isAuthenticated() {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

export function setActiveChatbotId(chatbotId, scope = null) {
  if (!canUseStorage()) return;

  const normalizedScope = normalizeScope(scope);
  const userScope = resolveUserScope(normalizedScope);
  const scopedKey = getActiveChatbotScopedKey(normalizedScope);

  if (!chatbotId) {
    window.localStorage.removeItem(scopedKey);
    window.localStorage.removeItem(ACTIVE_CHATBOT_STORAGE_KEY);
    window.localStorage.removeItem(ACTIVE_CHATBOT_LEGACY_OWNER_KEY);
    return;
  }

  const normalizedValue = String(chatbotId);
  window.localStorage.setItem(scopedKey, normalizedValue);

  // Legacy fallback keys (guarded by owner)
  window.localStorage.setItem(ACTIVE_CHATBOT_STORAGE_KEY, normalizedValue);
  window.localStorage.setItem(ACTIVE_CHATBOT_LEGACY_OWNER_KEY, String(userScope || 'anonymous'));
}

export function getActiveChatbotId(scope = null) {
  if (!canUseStorage()) return null;

  const scopedKey = getActiveChatbotScopedKey(scope);
  const scopedValue = window.localStorage.getItem(scopedKey);
  if (scopedValue) {
    return scopedValue;
  }

  const legacyValue = window.localStorage.getItem(ACTIVE_CHATBOT_STORAGE_KEY);
  const userScope = resolveUserScope(scope);
  const legacyOwner = window.localStorage.getItem(ACTIVE_CHATBOT_LEGACY_OWNER_KEY);

  if (!legacyValue) {
    return null;
  }

  // Never expose or migrate legacy values across users (multi-tenant safety).
  // If the current user cannot be resolved yet, fail closed instead of leaking a stale chatbot.
  if (userScope === "anonymous") {
    return legacyOwner === "anonymous" ? legacyValue : null;
  }

  if (legacyOwner && legacyOwner !== userScope) {
    return null;
  }

  if (!legacyOwner) {
    window.localStorage.setItem(ACTIVE_CHATBOT_LEGACY_OWNER_KEY, String(userScope));
  }

  window.localStorage.setItem(scopedKey, legacyValue);
  return legacyValue;
}

export function clearActiveChatbotId(scope = null) {
  if (!canUseStorage()) return;

  window.localStorage.removeItem(getActiveChatbotScopedKey(scope));
  window.localStorage.removeItem(ACTIVE_CHATBOT_STORAGE_KEY);
  window.localStorage.removeItem(ACTIVE_CHATBOT_LEGACY_OWNER_KEY);
}

export function setPublicWidgetSession(session, scope = null) {
  if (!canUseStorage()) return;

  const normalizedScope = {
    ...normalizeScope(scope),
    publicBotId: session?.publicBotId || normalizeScope(scope)?.publicBotId || null,
  };
  const scopedKey = getPublicWidgetSessionScopedKey(normalizedScope);

  if (!session) {
    window.localStorage.removeItem(scopedKey);
    window.localStorage.removeItem(PUBLIC_WIDGET_SESSION_STORAGE_KEY);
    return;
  }

  const payload = JSON.stringify(session);
  window.localStorage.setItem(scopedKey, payload);
  window.localStorage.setItem(PUBLIC_WIDGET_SESSION_STORAGE_KEY, payload);
}

export function getPublicWidgetSession(scope = null) {
  if (!canUseStorage()) return null;

  const scopedKey = getPublicWidgetSessionScopedKey(scope);
  const scopedRaw = window.localStorage.getItem(scopedKey);

  if (scopedRaw) {
    const parsed = safeJsonParse(scopedRaw);
    if (parsed) return parsed;
  }

  const legacyRaw = window.localStorage.getItem(PUBLIC_WIDGET_SESSION_STORAGE_KEY);
  if (!legacyRaw) return null;

  const legacySession = safeJsonParse(legacyRaw);
  const requestedPublicBotId =
    normalizeScope(scope)?.publicBotId ||
    (typeof window !== "undefined" ? window.__CHATBOT_PUBLIC_CONTEXT__?.publicBotId : null) ||
    null;

  if (!legacySession || !requestedPublicBotId) return null;
  if (legacySession.publicBotId && legacySession.publicBotId !== requestedPublicBotId) {
    return null;
  }

  window.localStorage.setItem(scopedKey, legacyRaw);
  return legacySession;
}

export function clearPublicWidgetSession(scope = null) {
  if (!canUseStorage()) return;

  window.localStorage.removeItem(getPublicWidgetSessionScopedKey(scope));
  window.localStorage.removeItem(PUBLIC_WIDGET_SESSION_STORAGE_KEY);
}

export function isPublicWidgetSessionExpired(session = null, scope = null) {
  const activeSession =
    session && typeof session === "object" && (session.token || session.createdAt)
      ? session
      : getPublicWidgetSession(scope || session);

  if (!activeSession?.token || !activeSession?.createdAt || !activeSession?.expiresInSeconds) {
    return true;
  }

  const expiryMs = Number(activeSession.createdAt) + Number(activeSession.expiresInSeconds) * 1000;
  return Date.now() >= Math.max(expiryMs - 10_000, Number(activeSession.createdAt));
}
